<template>
  <div class="product-line-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-data-analysis"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'product-line-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { names: [], amounts: [], times: [] }

      const names = this.data.map(item => item.productLineName)
      const amounts = this.data.map(item => Number(item.totalAmount || 0))
      const times = this.data.map(item => Number(item.totalTimes || 0))

      return { names, amounts, times }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    /**
     * 智能格式化金额
     * @param {number} value - 金额值
     * @returns {string} 格式化后的金额字符串
     */
    formatAmount(value) {
      if (value === 0) return '¥0'

      // 金额大于等于1万,使用万为单位
      if (value >= 10000) {
        return '¥' + (value / 10000).toFixed(1) + 'w'
      }
      // 金额大于等于1000,显示千位
      else if (value >= 1000) {
        return '¥' + (value / 1000).toFixed(1) + 'k'
      }
      // 小于1000,直接显示
      else {
        return '¥' + value.toFixed(0)
      }
    },

    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      if (!this.hasData) {
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) return

      const { names, amounts, times } = this.chartData

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          }
        },
        legend: {
          data: ['支付金额', '订单数量'],
          top: 0,
          textStyle: {
            color: '#666'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: names,
          axisPointer: {
            type: 'shadow'
          },
          axisLine: {
            lineStyle: {
              color: '#e0e0e0'
            }
          },
          axisLabel: {
            color: '#666',
            interval: 0,
            rotate: names.length > 5 ? 30 : 0
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '支付金额',
            axisLabel: {
              formatter: value => this.formatAmount(value),
              color: '#666'
            },
            splitLine: {
              lineStyle: {
                type: 'dashed',
                color: '#e0e0e0'
              }
            }
          },
          {
            type: 'value',
            name: '订单数量',
            axisLabel: {
              formatter: value => value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value,
              color: '#666'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '支付金额',
            type: 'bar',
            data: amounts,
            barWidth: '40%',
            itemStyle: {
              borderRadius: [8, 8, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ])
            }
          },
          {
            name: '订单数量',
            type: 'line',
            yAxisIndex: 1,
            data: times,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 3,
              color: '#52c41a'
            },
            itemStyle: {
              color: '#52c41a',
              borderColor: '#fff',
              borderWidth: 2
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.product-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 380px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  font-size: 14px;
}

.chart-loading i,
.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}
</style>




// WEBPACK FOOTER //
// src/custom/components/stats/pay-stats/components/product-line-chart.vue